import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';

interface WindowSize {
  width: number;
  height: number;
}

// Create context with an undefined default value
const WindowSizeContext = createContext<WindowSize | undefined>(undefined);

interface WindowSizeProviderProps {
  children: ReactNode;
}

export const WindowSizeProvider: React.FC<WindowSizeProviderProps> = ({ children }) => {
  // Initialize state with the current window dimensions
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    // Handler to update state on resize
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return <WindowSizeContext.Provider value={windowSize}>{children}</WindowSizeContext.Provider>;
};

// Custom hook for consuming window size context
export const useWindowSize = (): WindowSize => {
  const context = useContext(WindowSizeContext);
  if (context === undefined) {
    throw new Error('useWindowSize must be used within a WindowSizeProvider');
  }
  return context;
};
